/**
 * Percentages reach the reader as numbers from EPUB records and as strings
 * from the PDF payload, so both forms have to answer here.
 * @param {number|string} value
 * @returns {number|null} The percentage, or null when it is unusable
 */
function percentage(value) {
    const parsed = typeof value === 'number' ? value : Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
}

/**
 * The page an anchor names, as the number the reader counts it by.
 *
 * Pages reach the reader as numbers from EPUB records and as strings from the
 * PDF payload, and they are one-based wherever the reader counts them, so
 * anything below 1 names no page at all.
 *
 * @param {number|string|null|undefined} value - An anchor's page, any form
 * @returns {number|null} The page, or null when it names none
 */
export function anchorPageNumber(value) {
    const page = percentage(value);
    return page !== null && page >= 1 ? page : null;
}

/**
 * A host anchor with its page read as the number it names.
 *
 * The page badge matches an anchor's page against the numbers a viewer
 * reports on screen, and `Set.has` does not coerce — a `page` of `"3"` would
 * hide the piece with nothing to show why. An anchor that names no page (a
 * fixed-layout one keys by its locator's href) or names one this cannot read
 * is left exactly as it came, identity included.
 *
 * @param {object} addOn - A record that already carries an anchor
 * @returns {object} The same record, or a copy whose anchor page is a number
 */
function withNumericPage(addOn) {
    const { page } = addOn.anchor;
    if (page == null || typeof page === 'number') return addOn;

    const parsed = percentage(page);
    if (parsed === null) return addOn;

    return { ...addOn, anchor: { ...addOn.anchor, page: parsed } };
}

/**
 * Gives an add-on its anchor.
 *
 * A record that already carries one is returned as it came. A legacy PDF
 * record — start_page plus an optional location_in_start_page — gets the
 * anchor those fields describe, so the store holds one shape whichever era
 * the payload is from. The legacy fields stay on the record as they came:
 * this adds `anchor`, it does not rewrite the host's payload.
 *
 * @param {object} addOn - An add-on record, any era
 * @returns {object} The same record, or a copy carrying `anchor`
 */
export function normalizeAddOnAnchor(addOn) {
    if (!addOn) return addOn;
    if (addOn.anchor) return withNumericPage(addOn);

    const page = anchorPageNumber(addOn.start_page);
    if (page === null) return addOn;

    const x = percentage(addOn.location_in_start_page?.x);
    const y = percentage(addOn.location_in_start_page?.y);
    const anchor = x !== null && y !== null ? { type: 'point', page, point: { x, y } } : { type: 'page', page };

    return { ...addOn, anchor };
}

/**
 * Places point-anchored add-ons on the pages currently on screen.
 *
 * Format-blind on purpose: the caller says which pages are visible and how an
 * anchor names its page. EPUB keys by the locator's href — one spine file is
 * one fixed-layout page, and the href is stable where the page number is not —
 * and PDF keys by the page number.
 *
 * @param {Array<object>} addOns - Add-on records, any anchor type
 * @param {Array<{key: string|number, page: number|null, rect: DOMRect}>|null|undefined} pages - The pages on screen, if any have been measured yet
 * @param {{ keyOf: (addOn: object) => string|number|undefined }} options - How an anchor names its page
 * @returns {Array<{addOn: object, key: string|number, left: number, top: number}>} Viewport pixels
 */
export function resolvePointAnchors(addOns, pages, { keyOf }) {
    const rectByKey = new Map((pages ?? []).map(page => [page.key, page.rect]));

    const resolved = [];
    for (const addOn of addOns ?? []) {
        const anchor = addOn?.anchor;
        if (anchor?.type !== 'point') continue;

        const key = keyOf(addOn);
        const rect = rectByKey.get(key);
        if (!rect) continue;

        const x = percentage(anchor.point?.x);
        const y = percentage(anchor.point?.y);
        if (x === null || y === null) continue;

        resolved.push({
            addOn,
            key,
            left: rect.left + (x / 100) * rect.width,
            top: rect.top + (y / 100) * rect.height,
        });
    }
    return resolved;
}

/**
 * The point-anchored add-ons on the given pages, with their coordinates as
 * numbers, in input order.
 *
 * For a host that positions with CSS — a PDF page, where `left: 25%` is the
 * anchor as it is — this is `resolvePointAnchors` minus the rect. Coordinates
 * arrive as numbers or strings like every other percentage here.
 *
 * @param {Array<object>|null|undefined} addOns - Add-on records, any anchor type
 * @param {Set<string|number>} keys - The pages on screen, as the anchors name them
 * @param {{ keyOf: (addOn: object) => string|number|undefined }} options - How an anchor names its page
 * @returns {Array<{addOn: object, x: number, y: number}>} Percentages of the page
 */
export function pointAnchoredAddOns(addOns, keys, { keyOf }) {
    const resolved = [];
    for (const addOn of addOns ?? []) {
        if (addOn?.anchor?.type !== 'point') continue;

        const key = keyOf(addOn);
        if (key == null || !keys.has(key)) continue;

        const x = percentage(addOn.anchor.point?.x);
        const y = percentage(addOn.anchor.point?.y);
        if (x === null || y === null) continue;

        resolved.push({ addOn, x, y });
    }
    return resolved;
}
